import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Alert,
  Box,
  Checkbox,
  Divider,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNotification } from '../../../hooks/NotificationContext';
import { getPreferences, updatePreferences } from '../../../modules/notification';

const CHANNEL_LABELS = {
  inapp: 'In-app',
  slack: 'Slack',
};

const UserNotificationSettings = ({ projectId, isAdmin }) => {
  const { addNotification } = useNotification();
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['notificationPreferences', projectId],
    queryFn: () => getPreferences(projectId),
    enabled: !!projectId,
    staleTime: 30_000,
  });

  // snake_case from backend
  const slackEnabled = data?.slack_enabled ?? false;
  const preferences = data?.preferences ?? [];

  // Slack column is only shown when Slack is active for this project
  const visibleChannels = slackEnabled ? ['inapp', 'slack'] : ['inapp'];
  const columnCount = visibleChannels.length;

  const { mutate: toggle, isPending: togglePending, variables: toggleVariables } = useMutation({
    mutationFn: ({ category, channel, enabled }) =>
      updatePreferences(projectId, [{ category, channel, enabled }]),

    onMutate: async ({ category, channel, enabled }) => {
      await queryClient.cancelQueries({ queryKey: ['notificationPreferences', projectId] });
      const previous = queryClient.getQueryData(['notificationPreferences', projectId]);

      queryClient.setQueryData(['notificationPreferences', projectId], (old) => ({
        ...old,
        preferences: old.preferences.map((pref) =>
          pref.category === category
            ? { ...pref, channels: { ...pref.channels, [channel]: enabled } }
            : pref,
        ),
      }));

      return { previous };
    },

    onError: (err, _vars, context) => {
      queryClient.setQueryData(['notificationPreferences', projectId], context.previous);
      addNotification('danger', err?.response?.data?.error ?? err.message);
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['notificationPreferences', projectId] });
    },
  });

  const handleToggle = (category, channel, currentValue) => {
    const isThisTogglePending =
      togglePending &&
      toggleVariables?.category === category &&
      toggleVariables?.channel === channel;

    if (isThisTogglePending) return;
    toggle({ category, channel, enabled: !currentValue });
  };

  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="text" width={200} height={28} sx={{ mb: 1 }} />
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} variant="rounded" height={64} sx={{ mb: 1.5 }} />
        ))}
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error?.response?.data?.error ?? error?.message ?? 'Failed to load notification preferences.'}
      </Alert>
    );
  }

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
        overflow: 'hidden',
        mt: 3,
      }}
    >
      {/* Header */}
      <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle1" fontWeight={700}>
          My Notification Preferences
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Choose how you want to be notified for each event in this project.
          {slackEnabled && ' Slack notifications are sent to your Slack DM.'}
        </Typography>

        {!slackEnabled && (
          <Alert
            severity="info"
            icon={<InfoOutlinedIcon fontSize="small" />}
            sx={{ mt: 1.5, py: 0.5, fontSize: 13 }}
          >
            {isAdmin? 'Slack is not connected for this project. Please connect Slack to enable this feature':'Slack is not connected for this project. Contact a project admin to enable it.'}
          </Alert>
        )}
      </Box>

      {/* Column headers */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `1fr repeat(${columnCount}, 100px)`,
          px: 3,
          py: 1,
          bgcolor: 'action.hover',
        }}
      >
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          Event
        </Typography>
        {visibleChannels.map((ch) => (
          <Typography
            key={ch}
            variant="caption"
            color="text.secondary"
            fontWeight={600}
            sx={{ textAlign: 'center' }}
          >
            {CHANNEL_LABELS[ch]}
          </Typography>
        ))}
      </Box>

      <Divider />

      {preferences.length === 0 ? (
        <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No notification categories available.
          </Typography>
        </Box>
      ) : (
        <Stack divider={<Divider />}>
          {preferences.map((pref) => (
            <Box
              key={pref.category}
              sx={{
                display: 'grid',
                gridTemplateColumns: `1fr repeat(${columnCount}, 100px)`,
                alignItems: 'center',
                px: 3,
                py: 1.5,
                '&:hover': { bgcolor: 'action.hover' },
                transition: 'background 0.15s',
              }}
            >
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  {pref.label}
                </Typography>
                {pref.description && (
                  <Typography variant="caption" color="text.secondary">
                    {pref.description}
                  </Typography>
                )}
              </Box>

              {visibleChannels.map((channel) => {
                const checked = pref.channels?.[channel] ?? false;
                const isThisTogglePending =
                  togglePending &&
                  toggleVariables?.category === pref.category &&
                  toggleVariables?.channel === channel;

                return (
                  <Box key={channel} sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Checkbox
                      size="small"
                      checked={checked}
                      disabled={isThisTogglePending}
                      onChange={() => handleToggle(pref.category, channel, checked)}
                      sx={{ p: 0.5 }}
                    />
                  </Box>
                );
              })}
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default UserNotificationSettings;